/**
 * Athlete Predictive Search Integration
 * Listens for search input and renders athlete results outside the morph target.
 */
import { searchAthletes, fetchAllAthletes } from './athlete-api.js';

let debounceTimer = null;

function init() {
  // Pre-warm the cache on first interaction
  let warmed = false;
  document.addEventListener('focusin', (e) => {
    if (!warmed && e.target?.classList?.contains('search-input')) {
      warmed = true;
      fetchAllAthletes().catch(() => {});
    }
  }, { once: false });

  // Listen for input on any search input
  document.addEventListener('input', (e) => {
    if (!e.target?.classList?.contains('search-input')) return;

    clearTimeout(debounceTimer);
    const query = e.target.value.trim();

    if (query.length < 2) {
      hideAthleteResults();
      return;
    }

    debounceTimer = setTimeout(() => {
      searchAthletes(query, 5).then(renderAthleteResults).catch(() => hideAthleteResults());
    }, 200);
  });

  // Clear athlete results when search resets
  document.addEventListener('click', (e) => {
    if (e.target?.closest('.predictive-search__reset-button') || e.target?.closest('.predictive-search__close-modal-button')) {
      hideAthleteResults();
    }
  });
}

function getOrCreateContainer() {
  let container = document.getElementById('athlete-search-results');
  if (container) return container;

  // Find the morph target and insert before it (so athletes appear above other results)
  const morphTarget = document.querySelector('.predictive-search-form__content');
  if (!morphTarget) return null;

  container = document.createElement('div');
  container.id = 'athlete-search-results';
  morphTarget.parentNode.insertBefore(container, morphTarget);
  return container;
}

function renderAthleteResults(athletes) {
  const container = getOrCreateContainer();
  if (!container) return;

  if (!athletes.length) {
    hideAthleteResults();
    return;
  }

  container.innerHTML = `
    <div class="athlete-search-results__inner">
      <h4 class="predictive-search-results__title" style="padding-inline: var(--padding-xl);">Athletes</h4>
      <ul class="athlete-search-results__list list-unstyled" role="listbox" style="padding-inline: var(--padding-xl);">
        ${athletes.map(a => `
          <li class="athlete-search-results__item">
            <a href="${a.url}" class="athlete-search-results__link">
              ${a.image
                ? `<img class="athlete-search-results__image" src="${a.image}" alt="${a.name}" width="40" height="40" loading="lazy">`
                : `<span class="athlete-search-results__placeholder">${a.name.charAt(0)}</span>`
              }
              <span class="athlete-search-results__info">
                <span class="athlete-search-results__name">${a.name}</span>
                ${a.country ? `<span class="athlete-search-results__country">${a.country}</span>` : ''}
              </span>
            </a>
          </li>
        `).join('')}
      </ul>
    </div>
  `;
  container.hidden = false;
}

function hideAthleteResults() {
  const container = document.getElementById('athlete-search-results');
  if (container) {
    container.innerHTML = '';
    container.hidden = true;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
